// src/pages/qc/HmAnalysisModal.jsx
import { useState, useMemo } from "react";
import { todayDubai } from "../../utils/dateUtils";

const EMPTY = {
  date: todayDubai(),
  hmId: "",
  batchNo: "",
  viscosity: "",
  viscosityTempC: "",
  softeningPt: "",
  setTime: "",
  openTime: "",
  peelAdhesion: "",
  loopTack: "",
  remarks: "",
};

function num(v) { return v === "" ? null : parseFloat(v); }

function Row({ label, children }) {
  return (
    <div className="form-row">
      <span className="form-label">{label}</span>
      <div style={{ display:"flex", gap:6, alignItems:"center", flex:1 }}>{children}</div>
    </div>
  );
}

function NumInput({ value, onChange, placeholder, width = 110 }) {
  return (
    <input type="number" className="form-input" value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder} step="any" style={{ width }} />
  );
}

function specHint(min, max, unit) {
  if (min == null && max == null) return null;
  const r = (min != null && max != null)
    ? `${min} – ${max}`
    : min != null ? `≥ ${min}` : `≤ ${max}`;
  return `spec: ${r}${unit ? " " + unit : ""}`;
}

export function HmAnalysisModal({ specs, grades = [], onSave, onCancel }) {
  const [form,   setForm]   = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  function set(field, value) { setForm(f => ({ ...f, [field]: value })); }

  const displayNameMap = useMemo(() => {
    const m = {};
    grades.forEach(g => { m[g.id] = g.displayName || g.id; });
    return m;
  }, [grades]);

  const selectedSpec = useMemo(
    () => specs.find(s => s.hmId === form.hmId) ?? null,
    [specs, form.hmId]
  );

  // PSA products have peel adhesion; HM products have set/open time
  const isPSA = selectedSpec
    ? (selectedSpec.peelAdhesionMin != null || selectedSpec.peelAdhesionMax != null)
    : false;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.hmId)           { setError("Product is required.");  return; }
    if (!form.batchNo.trim()) { setError("Batch No is required."); return; }
    setSaving(true); setError(null);
    try {
      await onSave({
        date:           form.date,
        hmId:           form.hmId,
        batchNo:        form.batchNo.trim(),
        viscosity:      num(form.viscosity),
        viscosityTempC: num(form.viscosityTempC),
        softeningPt:    num(form.softeningPt),
        setTime:        num(form.setTime),
        openTime:       num(form.openTime),
        peelAdhesion:   num(form.peelAdhesion),
        loopTack:       num(form.loopTack),
        remarks:        form.remarks.trim() || null,
      });
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  const sortedSpecs = [...specs].sort((a, b) =>
    (displayNameMap[a.hmId] || a.hmId).localeCompare(displayNameMap[b.hmId] || b.hmId)
  );
  const s = selectedSpec;

  return (
    <div className="modal wide">
      <div className="modal-header">
        <h3 className="modal-title">Add Hotmelt Analysis</h3>
        <button className="btn" onClick={onCancel}>✕</button>
      </div>

      <form onSubmit={handleSubmit}>
        <Row label="Production Date">
          <input type="date" className="form-input" value={form.date}
            onChange={e => set("date", e.target.value)} required style={{ width:160 }} />
        </Row>

        <Row label="Product *">
          <select className="form-select" value={form.hmId}
            onChange={e => set("hmId", e.target.value)} style={{ flex:1 }}>
            <option value="">— select grade —</option>
            {sortedSpecs.map(sp => (
              <option key={sp.hmId} value={sp.hmId}>
                {displayNameMap[sp.hmId] || sp.hmId}{sp.appearance ? ` — ${sp.appearance}` : ""}
              </option>
            ))}
          </select>
        </Row>

        <Row label="Batch No *">
          <input className="form-input" placeholder="e.g. B-2501" value={form.batchNo}
            onChange={e => set("batchNo", e.target.value)} style={{ flex:1 }} />
        </Row>

        <div className="form-sep" />

        <Row label="Viscosity (cps) *">
          <NumInput value={form.viscosity} onChange={v => set("viscosity", v)}
            placeholder={s ? `${s.viscosityMin}–${s.viscosityMax}` : "value"} />
          <span style={{ fontSize:12, color:"#888" }}>@</span>
          <NumInput value={form.viscosityTempC} onChange={v => set("viscosityTempC", v)}
            placeholder={s?.viscosityTempC ?? "°C"} width={75} />
          <span style={{ fontSize:12, color:"#888" }}>°C</span>
          {s && <span style={{ fontSize:11, color:"#aaa" }}>{specHint(s.viscosityMin, s.viscosityMax, "cps")}</span>}
        </Row>

        <Row label="Softening Pt (°C) *">
          <NumInput value={form.softeningPt} onChange={v => set("softeningPt", v)}
            placeholder={s ? `${s.softeningPtMin}–${s.softeningPtMax}` : "value"} />
          {s && <span style={{ fontSize:11, color:"#aaa" }}>{specHint(s.softeningPtMin, s.softeningPtMax, "°C")}</span>}
        </Row>

        {/* HM-only fields */}
        {(!form.hmId || !isPSA) && <>
          <Row label="Set Time (s)">
            <NumInput value={form.setTime} onChange={v => set("setTime", v)}
              placeholder={s ? `${s.setTimeMin ?? ""}–${s.setTimeMax ?? ""}` : "value"} />
            {s && s.setTimeMax != null && <span style={{ fontSize:11, color:"#aaa" }}>{specHint(s.setTimeMin, s.setTimeMax, "s")}</span>}
          </Row>
          <Row label="Open Time (s)">
            <NumInput value={form.openTime} onChange={v => set("openTime", v)}
              placeholder={s ? `${s.openTimeMin ?? ""}–${s.openTimeMax ?? ""}` : "value"} />
            {s && s.openTimeMax != null && <span style={{ fontSize:11, color:"#aaa" }}>{specHint(s.openTimeMin, s.openTimeMax, "s")}</span>}
          </Row>
        </>}

        {/* PSA-only fields */}
        {isPSA && <>
          <Row label="Peel Adhesion (kg/in)">
            <NumInput value={form.peelAdhesion} onChange={v => set("peelAdhesion", v)}
              placeholder={s ? `${s.peelAdhesionMin}–${s.peelAdhesionMax}` : "value"} />
            {s && <span style={{ fontSize:11, color:"#aaa" }}>{specHint(s.peelAdhesionMin, s.peelAdhesionMax, "kg/in")}</span>}
          </Row>
          <Row label="Loop Tack (kg/in²)">
            <NumInput value={form.loopTack} onChange={v => set("loopTack", v)}
              placeholder={s ? `${s.loopTackMin}–${s.loopTackMax}` : "value"} />
            {s && <span style={{ fontSize:11, color:"#aaa" }}>{specHint(s.loopTackMin, s.loopTackMax, "kg/in²")}</span>}
          </Row>
        </>}

        <div className="form-sep" />

        <Row label="Remarks">
          <textarea className="form-input" rows={2} placeholder="Optional notes…"
            value={form.remarks} onChange={e => set("remarks", e.target.value)}
            style={{ flex:1, resize:"vertical" }} />
        </Row>

        {error && <div className="alert-warn" style={{ marginTop:8 }}>{error}</div>}

        <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:16 }}>
          <button type="button" className="btn" onClick={onCancel} disabled={saving}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}
